
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthProvider';
import { useNavigation } from '../contexts/NavigationProvider';
import SparklesIcon from './icons/SparklesIcon';
import UsersIcon from './icons/UsersIcon';
import * as api from '../services/apiService';

export const SeedDashboardContent: React.FC = () => {
    const { currentUser } = useAuth();
    const { handleViewProfile } = useNavigation();
    const [stats, setStats] = useState<any>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.getSeedDashboardStats()
            .then(statsData => setStats(statsData))
            .finally(() => setLoading(false));
    }, []);

    if (loading) return <div className="p-8 text-center text-brand-muted animate-pulse font-urbanist">Fetching lineage data...</div>;
    if (!stats) return null;

    const recruits = stats.recruits || [];

    return (
        <div className="space-y-6 font-urbanist">
            <div className="bg-brand-surface rounded-lg p-6 border border-brand-orange/30 shadow-lg shadow-brand-orange/5">
                <div className="flex items-center justify-between mb-4 flex-wrap gap-4">
                    <div className="flex items-center space-x-3">
                        <SparklesIcon className="h-8 w-8 text-brand-orange" />
                        <div>
                            <h2 className="text-2xl font-extrabold text-white tracking-tight">Founder's Hub</h2>
                            <p className="text-brand-muted text-sm">Lineage Phase (Season 1)</p>
                        </div>
                    </div>
                    <div className="bg-gray-800 px-4 py-2 rounded-xl border border-gray-700">
                        <p className="text-[10px] text-brand-muted tracking-widest font-bold">Voucher Status</p>
                        <p className="text-2xl font-bold text-white">{stats.vouchersUsed} / {stats.voucherLimit}</p>
                    </div>
                </div>
                
                <div className="bg-brand-orange/10 p-4 rounded-xl border border-brand-orange/20 mb-6">
                    <h4 className="font-bold text-white mb-1 tracking-tight text-sm">Recruitment Advantage</h4>
                    <p className="text-xs text-gray-300 leading-relaxed">
                        Your recruits get <span className="text-brand-orange font-bold">2 Years of Pro or Pro+ with 20% discount</span>. Focus on scouting active performers, managers, and venue owners to maximize your lineage pool.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-gray-800/40 p-5 rounded-xl border border-gray-800">
                        <p className="text-[10px] text-brand-muted mb-1 font-bold tracking-widest">Est. Lineage Pool</p>
                        <p className="text-3xl font-bold text-brand-orange">€{(recruits.length * 15).toLocaleString()}</p>
                        <p className="text-[10px] text-gray-400 mt-1 italic">Projected Quarterly Bonus Share</p>
                    </div>
                    <div className="bg-gray-800/40 p-5 rounded-xl border border-gray-800">
                        <p className="text-[10px] text-brand-muted mb-1 font-bold tracking-widest">Active Retention</p>
                        <p className="text-3xl font-bold text-white">100%</p>
                        <p className="text-[10px] text-gray-400 mt-1 italic">Pro+ Conversion Rate</p>
                    </div>
                </div>
            </div>

            <div className="bg-brand-surface rounded-lg p-6 border border-gray-800">
                <h3 className="text-xl font-bold text-white mb-6 tracking-tight">Live Lineage</h3>
                {recruits.length > 0 ? (
                    <div className="overflow-x-auto custom-scrollbar">
                        <table className="w-full text-left text-sm">
                            <thead className="text-brand-muted text-[10px] font-bold border-b border-gray-800 tracking-widest">
                                <tr>
                                    <th className="pb-4 px-2">Artist / Business</th>
                                    <th className="pb-4 px-2">Core Role</th>
                                    <th className="pb-4 px-2">Access Status</th>
                                    <th className="pb-4 px-2 text-right">Last Sync</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-800">
                                {recruits.map((recruit: any) => (
                                    <tr key={recruit.handle} className="hover:bg-gray-800/20 transition-colors cursor-pointer" onClick={() => handleViewProfile(recruit)}>
                                        <td className="py-4 px-2 flex items-center gap-3">
                                            <img src={recruit.avatarUrl} className="h-10 w-10 rounded-full border border-gray-700" alt="" />
                                            <div>
                                                <p className="font-bold text-white group-hover:text-brand-orange transition-colors">{recruit.name}</p>
                                                <p className="text-[10px] text-brand-muted font-mono">@{recruit.handle}</p>
                                            </div>
                                        </td>
                                        <td className="py-4 px-2 text-gray-300 font-medium">{recruit.role || 'Member'}</td>
                                        <td className="py-4 px-2">
                                            <span className="bg-green-500/10 text-green-400 px-3 py-1 rounded-full text-[10px] font-bold border border-green-500/20">PRO+ TRIAL</span>
                                        </td>
                                        <td className="py-4 px-2 text-right text-[10px] text-brand-muted font-bold">ALIVE</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="text-center py-16 bg-gray-800/20 rounded-xl border border-dashed border-gray-700">
                        <UsersIcon className="h-12 w-12 text-gray-700 mx-auto mb-4" />
                        <p className="text-brand-muted text-sm italic">No recruits found in your lineage yet.</p>
                        <p className="text-brand-orange font-bold text-xs tracking-widest mt-2">Ready to scout your first artist?</p>
                    </div>
                )}
            </div>

            <div className="bg-brand-surface rounded-lg p-6 border border-gray-800">
                <h3 className="text-xl font-bold text-white mb-2 tracking-tight">Your Invitation Code</h3>
                <p className="text-sm text-brand-muted mb-6">Share this code with your professional circle to build your pool.</p>
                <div className="bg-gray-900 p-6 rounded-2xl flex flex-col md:flex-row justify-between items-center border border-brand-orange/30 gap-6">
                    <code className="text-4xl font-bold text-brand-orange tracking-[0.2em] font-mono">{currentUser?.referralCode}</code>
                    <button 
                        className="w-full md:w-auto bg-brand-orange text-white px-8 py-4 rounded-full font-bold hover:bg-orange-600 transition-all shadow-lg shadow-brand-orange/20 tracking-widest text-sm" 
                        onClick={() => {
                            navigator.clipboard.writeText(currentUser?.referralCode || '');
                            alert('Code copied to clipboard!');
                        }}
                    >
                        Copy Scout Code
                    </button>
                </div>
            </div>
        </div>
    );
};

const SeedDashboard: React.FC = () => {
    return <SeedDashboardContent />;
}

export default SeedDashboard;
